import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from "react";
import { ApolloProvider as ApolloProviderRaw } from "@apollo/client/react";
import { AccountType, getExternalPlatformIdFromInternalPlatformId, getVirtualEnvironment, notEmpty, } from "@orb-labs/types";
import { createApolloClient } from "../graphql/createApolloClient.js";
import { usePlatformGraphQL, useResolvedApolloCache } from "../graphql/PlatformGraphQLProvider.js";
import { useOrby } from "../components/OrbyProvider/OrbyProvider.js";
import { BullrunUseQueryProvider } from "../hooks/bullrunQueries/BullrunUseQueryContext.js";
// ApolloProvider is typed against @types/react@18 which lacks bigint in ReactNode.
const ApolloProvider = ApolloProviderRaw;
/**
 * Context-aware GraphQL provider that builds the Orby URL from the current
 * account cluster and manages Apollo client lifecycle.
 *
 * Reads configuration from `PlatformGraphQLProvider` context (baseUrlPrefix,
 * initializeCache, webSocketImpl, useQueryLike, observability).
 *
 * Mounted by `BullrunProviders` alongside `LiveSubscriptionInterestsProvider`
 * and `LiveUpdatesProvider`.
 */
export const GraphQLProvider = ({ children }) => {
    const { baseUrlPrefix, webSocketImpl, useQueryLike, observability } = usePlatformGraphQL();
    // Reuse the cache that PlatformGraphQLProvider already resolved from
    // initializeCache(). Running initializeCache a second time here would create
    // a second persister writing to the same AsyncStorage key and racing flushes
    // with the base client's cache.
    const resolvedCache = useResolvedApolloCache();
    const { accountCluster, supportedPlatforms } = useOrby();
    const defaultUrl = `${baseUrlPrefix}/mainnet`;
    const [orbyUrl, setOrbyUrl] = useState(defaultUrl);
    // Client lifecycle lives in useEffect rather than render so that React
    // Strict Mode's double-invocation in dev — and concurrent rendering in
    // general — can't create an orphaned ApolloClient (WebSocket + HTTP links)
    // that the cleanup never sees. The effect re-runs when any of the inputs
    // baked into the client change, calls `.stop()` on the previous instance
    // in cleanup, and installs the new one via state.
    //
    // We bundle the client with the URL it was built for. During the initial
    // public-mainnet → account-cluster transition we keep rendering the previous
    // client until the effect installs the new one. Other URL changes (account
    // switch, logout) still gate on a matching client — see `renderClient` below.
    const [clientEntry, setClientEntry] = useState(null);
    useEffect(() => {
        const nextClient = createApolloClient({
            baseUrl: orbyUrl,
            getToken: () => Promise.resolve(null),
            observability,
            webSocketImpl,
            ...(resolvedCache ? { cache: resolvedCache } : {}),
        });
        setClientEntry({ client: nextClient, url: orbyUrl });
        return () => {
            nextClient.stop();
        };
    }, [orbyUrl, resolvedCache, webSocketImpl, observability]);
    const computeOrbyUrl = useCallback(() => {
        if (!accountCluster?.idempotencyKey || accountCluster.accounts.length === 0) {
            setOrbyUrl(defaultUrl);
            return;
        }
        try {
            let matchingPlatformId;
            let matchingAccount;
            for (const platform of supportedPlatforms) {
                const vmType = getVirtualEnvironment(platform.platformId);
                matchingAccount = accountCluster.accounts.find((account) => (notEmpty(account.platformId) && account.platformId === platform.platformId) ||
                    (account.type === AccountType.EOA && vmType === account.vmType));
                if (matchingAccount) {
                    matchingPlatformId = platform.platformId;
                    break;
                }
            }
            // No matching platform: fall through to the mainnet URL rather than
            // calling getExternalPlatformIdFromInternalPlatformId(undefined as bigint),
            // which throws and trips the catch below with a noisy error log.
            if (!notEmpty(matchingPlatformId) || !matchingAccount) {
                setOrbyUrl((prev) => (prev !== defaultUrl ? defaultUrl : prev));
                return;
            }
            const orbyPlatformId = getExternalPlatformIdFromInternalPlatformId(matchingPlatformId);
            const url = orbyPlatformId
                ? `${baseUrlPrefix}/${orbyPlatformId}/${accountCluster.idempotencyKey}/${matchingAccount.address}`
                : defaultUrl;
            setOrbyUrl((prevUrl) => (prevUrl !== url ? url : prevUrl));
        }
        catch (error) {
            observability.error("Failed to compute Orby URL:", error);
            setOrbyUrl(defaultUrl);
        }
    }, [
        accountCluster?.accounts,
        accountCluster?.idempotencyKey,
        supportedPlatforms,
        baseUrlPrefix,
        defaultUrl,
        observability,
    ]);
    useEffect(() => {
        computeOrbyUrl();
    }, [computeOrbyUrl]);
    // Keep children mounted only for the public-mainnet → account-cluster
    // transition (session restore). Account-to-account / logout switches wait
    // for the matching client so we don't query or cache against a stale endpoint.
    let renderClient = null;
    if (clientEntry) {
        if (clientEntry.url === orbyUrl || clientEntry.url === defaultUrl) {
            renderClient = clientEntry.client;
        }
    }
    if (!renderClient)
        return null;
    let content = children;
    if (useQueryLike) {
        content = _jsx(BullrunUseQueryProvider, { useQueryLike: useQueryLike, children: content });
    }
    return _jsx(ApolloProvider, { client: renderClient, children: content });
};
